import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import AgencyVideoLibrary from './AgencyVideoLibrary';
import AgencyVideoUploadModal from './AgencyVideoUploadModal';
import AgencyAnalyticsDashboard from './AgencyAnalyticsDashboard';
import AgencyTargetingPanel from './AgencyTargetingPanel';
import AgencyTeamPanel from './AgencyTeamPanel';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const AGENCY_JWT_KEY = 'agency_jwt';
const getAgencyToken = () => localStorage.getItem(AGENCY_JWT_KEY) || '';

// ─── Layout ─────────────────────────────────────────────────────────────────

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(160deg, #08080f 0%, #120822 100%);
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 58px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 200;
  flex-shrink: 0;
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Logo = styled.span`
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #ff4757;
  text-transform: uppercase;
`;

const ModeBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(255,71,87,0.12);
  border: 1px solid rgba(255,71,87,0.3);
  color: #ff7c89;
  border-radius: 20px;
  padding: 3px 10px;
  letter-spacing: 0.04em;
`;

const SwitchBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.45);
  border-radius: 20px;
  font-size: 0.72rem;
  padding: 3px 10px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: rgba(255,255,255,0.3); color: white; }
`;

const AgencyName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  letter-spacing: 0.01em;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.nav`
  width: 220px;
  flex-shrink: 0;
  border-right: 1px solid rgba(255,255,255,0.06);
  background: rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  padding: 24px 12px 20px;
  gap: 4px;
  overflow-y: auto;

  @media (max-width: 640px) {
    display: none;
  }
`;

const MobileNav = styled.div`
  display: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 8px 12px;
  gap: 4px;
  overflow-x: auto;
  background: rgba(0,0,0,0.2);

  @media (max-width: 640px) {
    display: flex;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: ${p => p.$active ? 'rgba(255,71,87,0.12)' : 'none'};
  color: ${p => p.$active ? '#ff4757' : 'rgba(255,255,255,0.55)'};
  font-size: 0.88rem;
  font-weight: ${p => p.$active ? '600' : '400'};
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  &:hover { background: rgba(255,255,255,0.06); color: white; }
`;

const NavIcon = styled.span`font-size: 1rem; flex-shrink: 0;`;

const UploadNavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  margin-top: 8px;
  border: 1px dashed rgba(255,71,87,0.35);
  border-radius: 10px;
  background: rgba(255,71,87,0.06);
  color: #ff7c89;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { background: rgba(255,71,87,0.12); border-color: rgba(255,71,87,0.6); color: #ff4757; }
`;

const NavSpacer = styled.div`flex: 1;`;

const NavDivider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.06);
  margin: 8px 4px;
`;

const Main = styled.main`
  flex: 1;
  padding: 32px 28px;
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: 20px 16px;
  }
`;

// ─── Settings section styled components ──────────────────────────────────────

const SettingsWrap = styled.div`color: white;`;

const SettingsTitle = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 28px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SettingsSection = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SettingsSectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const SField = styled.div`margin-bottom: 16px;`;

const SLabel = styled.label`
  display: block;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.45);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SInput = styled.input`
  width: 100%;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 12px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const SaveBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 22px;
  cursor: pointer;
  margin-top: 4px;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { background: #333; color: #555; cursor: default; }
`;

const SaveMsg = styled.span`
  font-size: 0.82rem;
  color: #2ecc71;
  margin-left: 12px;
`;

const ErrorMsg = styled.span`
  font-size: 0.82rem;
  color: #ff4757;
  margin-left: 12px;
`;

const EMPTY_TARGETING = { // excluded_publications added; publication exclusion UI in AgencyTargetingPanel
  target_genders: [],
  target_birth_year_min: null,
  target_birth_year_max: null,
  target_states: [],
  target_employment_statuses: [],
  target_income_ranges: [],
  excluded_publications: [],   // publication IDs this agency's ads should NOT appear in
};

// ─── Agency Settings panel ───────────────────────────────────────────────────

const AgencySettingsPanel = ({ profile, onSaved }) => {
  const [agencyName, setAgencyName] = useState(profile?.agency_name || '');
  const [website, setWebsite] = useState(profile?.website || '');
  const [contactEmail, setContactEmail] = useState(profile?.contact_email || '');
  const [targeting, setTargeting] = useState(profile?.default_targeting || EMPTY_TARGETING);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (profile) {
      setAgencyName(profile.agency_name || '');
      setWebsite(profile.website || '');
      setContactEmail(profile.contact_email || '');
      setTargeting(profile.default_targeting || EMPTY_TARGETING);
    }
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const token = getAgencyToken();
      const res = await fetch(`${API_URL}/api/agency/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          agency_name: agencyName.trim() || undefined,
          website: website.trim() || undefined,
          contact_email: contactEmail.trim() || undefined,
          default_targeting: targeting,
        }),
      });
      if (!res.ok) throw new Error(`Server error (${res.status})`);
      setMsg('Saved');
      onSaved?.({ agency_name: agencyName, website, contact_email: contactEmail, default_targeting: targeting });
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsWrap>
      <SettingsTitle>Agency Settings</SettingsTitle>

      <SettingsSection>
        <SettingsSectionTitle>Agency details</SettingsSectionTitle>
        <SField>
          <SLabel>Agency / Company name</SLabel>
          <SInput
            value={agencyName}
            onChange={e => setAgencyName(e.target.value)}
            placeholder="Acme Advertising Co."
          />
        </SField>
        <SField>
          <SLabel>Website</SLabel>
          <SInput
            value={website}
            onChange={e => setWebsite(e.target.value)}
            placeholder="https://acme-ads.com"
          />
        </SField>
        <SField>
          <SLabel>Billing / contact email</SLabel>
          <SInput
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
            placeholder="ads@acme.com"
          />
        </SField>
      </SettingsSection>

      <SettingsSection>
        <SettingsSectionTitle>Default ad targeting</SettingsSectionTitle>
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', margin: '0 0 16px' }}>
          These targeting criteria will be pre-filled when you upload a new ad. You can override them per video.
        </p>
        <AgencyTargetingPanel value={targeting} onChange={setTargeting} />
      </SettingsSection>

      <div>
        <SaveBtn onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </SaveBtn>
        {msg && <SaveMsg>{msg}</SaveMsg>}
        {err && <ErrorMsg>{err}</ErrorMsg>}
      </div>
    </SettingsWrap>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

function getJwtClaims() {
  try {
    const token = localStorage.getItem(AGENCY_JWT_KEY);
    if (!token) return {};
    return JSON.parse(atob(token.split('.')[1]));
  } catch { return {}; }
}

function getJwtAgencyName() {
  return getJwtClaims().agency_name || '';
}

function isOwner(claims) {
  return ['owner', 'agencyowner'].includes(claims?.role);
}

const AgencyDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('ads');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [agencyProfile, setAgencyProfile] = useState(null);
  const [libraryKey, setLibraryKey] = useState(0); // bump to re-fetch library
  // ownerRole starts from the JWT for instant render, then gets overridden by the live
  // role returned from /api/agency/profile (so admin role changes take effect on next load)
  const jwtClaims = getJwtClaims();
  const [ownerRole, setOwnerRole] = useState(isOwner(jwtClaims));

  const fetchProfile = useCallback(async () => {
    try {
      const token = getAgencyToken();
      const res = await fetch(`${API_URL}/api/agency/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAgencyProfile(data);
        // Override role with live value from server
        if (data.role) setOwnerRole(isOwner({ role: data.role }));
      }
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleLogout = () => {
    localStorage.removeItem(AGENCY_JWT_KEY);
    navigate('/agency/login', { replace: true });
  };

  const handleUploadCreated = () => {
    setShowUploadModal(false);
    setLibraryKey(k => k + 1);
    setActiveView('ads');
  };

  const navItems = [
    { key: 'ads',       icon: '🎬', label: 'My Ads' },
    { key: 'analytics', icon: '📊', label: 'Analytics' },
    ...(ownerRole ? [{ key: 'team', icon: '👥', label: 'Team' }] : []),
    ...(ownerRole ? [{ key: 'settings', icon: '⚙️', label: 'Settings' }] : []),
  ];

  return (
    <Shell>
      <TopBar>
        <TopLeft>
          <Logo>VuSub</Logo>
          <ModeBadge>Agency Portal</ModeBadge>
          {(agencyProfile?.agency_name || getJwtAgencyName()) && (
            <AgencyName>{agencyProfile?.agency_name || getJwtAgencyName()}</AgencyName>
          )}
        </TopLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SwitchBtn onClick={handleLogout}>Log out</SwitchBtn>
        </div>
      </TopBar>

      {/* Mobile nav strip */}
      <MobileNav>
        {navItems.map(n => (
          <NavItem key={n.key} $active={activeView === n.key} onClick={() => setActiveView(n.key)}
            style={{ width: 'auto', flex: '0 0 auto' }}>
            <NavIcon>{n.icon}</NavIcon>{n.label}
          </NavItem>
        ))}
        <NavItem $active={false} onClick={() => setShowUploadModal(true)} style={{ width: 'auto', flex: '0 0 auto', borderColor: 'rgba(255,71,87,0.35)', color: '#ff7c89' }}>
          <NavIcon>+</NavIcon>Upload ad
        </NavItem>
      </MobileNav>

      <Body>
        <Sidebar>
          {navItems.map(n => (
            <NavItem key={n.key} $active={activeView === n.key} onClick={() => setActiveView(n.key)}>
              <NavIcon>{n.icon}</NavIcon>{n.label}
            </NavItem>
          ))}

          <NavDivider />

          <UploadNavBtn onClick={() => setShowUploadModal(true)}>
            <NavIcon>+</NavIcon>Upload new ad
          </UploadNavBtn>

          <NavSpacer />

          <NavDivider />

          <NavItem $active={false} onClick={handleLogout} style={{ color: 'rgba(255,255,255,0.35)' }}>
            <NavIcon>🚪</NavIcon>Log out
          </NavItem>
        </Sidebar>

        <Main>
          {activeView === 'ads' && (
            <AgencyVideoLibrary
              key={libraryKey}
              agencyProfile={agencyProfile}
              onUploadClick={() => setShowUploadModal(true)}
            />
          )}
          {activeView === 'analytics' && <AgencyAnalyticsDashboard />}
          {activeView === 'team' && ownerRole && <AgencyTeamPanel />}
          {activeView === 'settings' && ownerRole && (
            <AgencySettingsPanel
              profile={agencyProfile}
              onSaved={updated => setAgencyProfile(updated)}
            />
          )}
        </Main>
      </Body>

      {showUploadModal && (
        <AgencyVideoUploadModal
          onClose={() => setShowUploadModal(false)}
          onCreated={handleUploadCreated}
          defaultTargeting={agencyProfile?.default_targeting}
        />
      )}
    </Shell>
  );
};

export default AgencyDashboard;
